import React from 'react';
import Icon from '@salesforce/design-system-react/components/icon';
import Checkbox from '@salesforce/design-system-react/components/checkbox';
import Input from '@salesforce/design-system-react/components/input';

type SettingsRowProps = {
  id: string;
  label: string;
  required?: boolean;
  tooltip?: string;
  toggleChecked?: boolean;
  showToggle?: boolean;
  onToggle?: (checked: boolean) => void;
  showInput?: boolean;
  inputValue?: string;
  onInputChange?: (val: string) => void;
  inputPlaceholder?: string;
  inputType?: React.ComponentProps<typeof Input>['type'];
  description?: string;
  showDescription?: boolean;
};

const SettingsRow: React.FC<SettingsRowProps> = ({
  id,
  label,
  required,
  tooltip,
  showToggle,
  toggleChecked,
  onToggle,
  showInput,
  inputValue,
  onInputChange,
  inputPlaceholder,
  inputType = 'text',
  description,
  showDescription,
}) => {
  const shouldRenderInput = showInput ?? (inputValue !== undefined || onInputChange !== undefined)
  return (
    <div
      className="slds-grid slds-gutters slds-grid_vertical-align-center slds-p-vertical_x-small slds-p-horizontal_small slds-wrap"
      style={{ width: '100%' }}
    >
      <div className="slds-col slds-size_12-of-12 slds-medium-size_3-of-12 slds-large-size_2-of-12 slds-shrink-none">
        <div className="slds-grid slds-grid_vertical-align-center">
          <span
            id={`${id}-label`}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              fontWeight: 600,
              fontSize: 14,
              marginBottom: '10px',
            }}
          >
            {required && <span style={{ color: 'red' }}>*</span>}
            <span className="slds-m-left_xx-small">{label}</span>
            {tooltip && (
              <span id="tooltip">
                <Icon
                  category="utility"
                  name="info"
                  size="x-small"
                  style={{
                    fill: 'rgb(133 125 125)',
                    marginBottom: '3px',
                    marginLeft: '5px',
                  }}
                />
                <span className="tooltiptext">{tooltip}</span>
              </span>
            )}
          </span>
        </div>
      </div>

      {showToggle && (
        <div className="slds-col slds-size_12-of-12 slds-medium-size_2-of-12 slds-large-size_1-of-12 slds-shrink-none">
          <Checkbox
            variant="toggle"
            checked={!!toggleChecked}
            onChange={(e: unknown, data: any) => onToggle?.(!!data?.checked)}
            labels={{ toggleEnabled: '', toggleDisabled: '' }}
            assistiveText={{ label: `${label} toggle` }}
          />
        </div>
      )}

      {shouldRenderInput && (
        <div className="slds-col slds-size_12-of-12 slds-medium-size_7-of-12 slds-large-size_4-of-12 slds-grow">
          <Input
            type={inputType}
            aria-labelledby={`${id}-label`}
            placeholder={inputPlaceholder}
            value={inputValue}
            onChange={(e: any, data?: { value?: string }) =>
              onInputChange?.(data?.value ?? e?.target?.value ?? "")
            }
          />
        </div>
      )}
      {!shouldRenderInput && (
        <div className='slds-col slds-grow' />
      )}
      {showDescription && (
        <div
          className={
            showInput
            ? 'slds-col slds-size_12-of-12 slds-medium-size_12-of-12 slds-large-size_2-of-12 slds-p-horizontal_small'
            : 'slds-col slds-size_12-of-12 slds-medium-size_7-of-12 slds-large-size_8-of-12 slds-p-horizontal_small'
          }
        >
          <span
            id={`desc-${id}`}
            className='slds-text-body_small slds-truncate'
            title={description}
          >
            {description}
          </span>
        </div>
      )}
    </div>
  );
};

export default SettingsRow;
